import React, { useState, useEffect } from 'react';
import { Calendar, Users, Wifi, BedDouble } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import BookingModal from '../components/BookingModal';
import AvailabilityChecker from '../components/AvailabilityChecker';
import RoomDetailsModal from '../components/RoomDetailsModal';
import apiClient from '../lib/apiClient';
import { useHotel } from '../context/HotelContext';
import { useAuth } from '../context/AuthContext';
import NoDataError from '../components/NoDataError';
import { useNavigate } from 'react-router-dom';

const mapRooms = (raw) => (raw || []).map(r => ({
  id: r.roomCode,
  roomTypeId: r.roomTypeId,
  name: r.roomType,
  type: r.roomType.toLowerCase().replace(/\s+/g, '-'),
  image: r.roomImageMap?.imageUrls?.[0] || '',
  images: r.roomImageMap?.imageUrls || [],
  price: r.price,
  occupancy: r.capacity,
  available: r.availableUnits > 0,
  availableUnits: r.availableUnits,
  totalQuantity: r.totalQuantity,
  taxPercent: r.taxPercent ?? 10,
  serviceChargePercent: r.serviceChargePercent ?? 0,
  description: `${r.roomType} — ${r.availableUnits} of ${r.totalQuantity} units available.`,
  features: (r.features || []),
  amenities: (r.amenities || []).map(a => ({ name: a })),
  rules: r.rules || [],
  beds: (r.beds || []),
  bedType: r.bedType || '',
  numberOfBeds: r.numberOfBeds || 1
}));

const Rooms = () => {
  const { hotel } = useHotel();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [roomType, setRoomType] = useState('all');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searching, setSearching] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [availabilityCheckerOpen, setAvailabilityCheckerOpen] = useState(false);
  const [roomDetailsOpen, setRoomDetailsOpen] = useState(false);
  const [selectedRoomForDetails, setSelectedRoomForDetails] = useState(null);
  const [apiRooms, setApiRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([{ id: 'all', name: 'All Rooms' }]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [roomsError, setRoomsError] = useState(false);

  const loadRooms = (raw) => {
    const rooms = mapRooms(raw);
    setApiRooms(rooms);
    const uniqueTypes = [...new Set(rooms.map(r => r.name))];
    setRoomTypes([{ id: 'all', name: 'All Rooms' }, ...uniqueTypes.map(t => ({ id: t.toLowerCase().replace(/\s+/g, '-'), name: t }))]);
  };

  useEffect(() => {
    apiClient.post('/api/Room/Get')
      .then(data => loadRooms(data?.availableRooms || []))
      .catch(() => setRoomsError(true))
      .finally(() => setLoadingRooms(false));
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut) { alert('Please fill in both check-in and check-out dates'); return; }
    if (new Date(checkOut) <= new Date(checkIn)) { alert('Check-out date must be after check-in date'); return; }

    setSearching(true);
    try {
      const data = await apiClient.post('/api/Search', {
        numberOfGuests: guests,
        checkInDate: checkIn,
        checkOutDate: checkOut,
      });
      loadRooms(data?.availableRooms || []);
      setSearchPerformed(true);
    } catch {
      alert('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleBooking = (room) => {
    if (!isLoggedIn) { navigate('/login', { state: { from: '/rooms' } }); return; }
    if (checkIn && checkOut) {
      setSelectedRoom({ ...room, prefilledData: { checkIn, checkOut, guests } });
      setBookingModalOpen(true);
    } else {
      setSelectedRoom(room);
      setAvailabilityCheckerOpen(true);
    }
  };

  const handleProceedToBook = (availabilityData) => {
    // Close availability checker and open booking modal with prefilled data
    setAvailabilityCheckerOpen(false);
    
    const roomWithBookingData = {
      ...selectedRoom,
      prefilledData: {
        checkIn: availabilityData.checkIn,
        checkOut: availabilityData.checkOut,
        guests: availabilityData.guests
      }
    };
    
    setSelectedRoom(roomWithBookingData);
    setBookingModalOpen(true);
  };

  const handleViewDetails = (room) => {
    setSelectedRoomForDetails(room);
    setRoomDetailsOpen(true);
  };

  const filteredRooms = roomType === 'all'
    ? apiRooms
    : apiRooms.filter(room => room.type === roomType);

  if (roomsError) {
    return <NoDataError message="We couldn't load room listings. Please try again later." />;
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.7), rgba(30, 64, 175, 0.7)), url(${hotel?.bannerUrl || ''})`
        }}
      >
        <div className="text-center text-white z-10 max-w-4xl px-6">
          <h1 className="mb-4 text-white">{hotel?.hotelName ? `Rooms at ${hotel.hotelName}` : 'Our Rooms'}</h1>
          <p className="text-xl">{hotel?.description || ''}</p>
        </div>
      </section>

      {/* Search Section */}
      <section className="section bg-white shadow-lg relative -mt-20 z-10">
        <div className="container">
          <Card className="p-8">
            <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline mr-2" size={16} />
                  Check-in Date
                </label>
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} // Cannot select past dates
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline mr-2" size={16} />
                  Check-out Date
                </label>
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  min={checkIn ? 
                    new Date(new Date(checkIn).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] :
                    new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  } // Cannot select check-out before check-in + 1 day
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="inline mr-2" size={16} />
                  Guests
                </label>
                <select
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Room Type
                </label>
                <select
                  value={roomType}
                  onChange={(e) => setRoomType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {roomTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-end">
                <Button type="submit" disabled={searching} className="w-full h-12">
                  {searching ? 'Searching...' : 'Search Rooms'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </section>

      {/* Rooms Section */}
      <section className="section-large">
        <div className="container">
          {searchPerformed && (
            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Search Results
              </h3>
              <p className="text-blue-700">
                Showing available rooms from {checkIn} to {checkOut} for {guests} guest{guests > 1 ? 's' : ''}
              </p>
            </div>
          )}

          <div className="space-y-8">
            {filteredRooms.map((room) => (
              <Card key={room.id} className="overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                  {/* Room Image */}
                  <div className="lg:col-span-1 relative aspect-video lg:aspect-square">
                    <img
                      src={room.image}
                      alt={room.name}
                      className="img-cover"
                    />
                    {!room.available && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold">
                          Not Available
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Room Details */}
                  <div className="lg:col-span-2 p-6 lg:p-8">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-6">
                      <div>
                        <h3 className="text-2xl font-semibold mb-2">{room.name}</h3>
                        <p className="text-gray-600 mb-4">{room.description}</p>
                        <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 mb-4">
                          {(room.beds?.length > 0 || room.bedType) && (
                            <span className="flex items-center gap-1.5">
                              <BedDouble size={15} className="text-blue-600" />
                              {room.beds?.length > 0
                                ? room.beds.map(b => `${b.count} ${b.bedName}`).join(' + ')
                                : `${room.numberOfBeds} ${room.bedType} Bed${room.numberOfBeds !== 1 ? 's' : ''}`
                              }
                            </span>
                          )}
                          <span className="flex items-center gap-1.5">
                            <Users size={15} className="text-blue-600" />
                            Max {room.occupancy} guest{room.occupancy !== 1 ? 's' : ''}
                          </span>
                          {room.availableUnits !== undefined && (
                            <span>{room.availableUnits} unit{room.availableUnits !== 1 ? 's' : ''} available</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center justify-end space-x-2 mb-2">
                          <span className="text-3xl font-bold text-blue-600">
                            ৳{room.price.toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">per night</p>
                      </div>
                    </div>

                    {/* Amenities */}
                    <div className="mb-6">
                      <h4 className="font-semibold mb-3">Amenities</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {room.amenities.map((amenity, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm text-gray-700">
                            <Wifi size={16} className="text-blue-600" />
                            <span>{amenity.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <h4 className="font-semibold mb-3">Room Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {room.features.map((feature, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Booking Button */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        onClick={() => handleBooking(room)}
                        disabled={!room.available}
                        className="flex-1"
                        variant={room.available ? "default" : "secondary"}
                      >
                        {room.available ? "Book Now" : "Not Available"}
                      </Button>
                      <Button 
                        onClick={() => handleViewDetails(room)}
                        variant="outline" 
                        className="flex-1"
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {loadingRooms && (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          {!loadingRooms && filteredRooms.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No rooms found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Availability Checker */}
      {availabilityCheckerOpen && (
        <AvailabilityChecker
          room={selectedRoom}
          onProceedToBook={handleProceedToBook}
          onClose={() => setAvailabilityCheckerOpen(false)}
        />
      )}

      <RoomDetailsModal
        isOpen={roomDetailsOpen}
        onClose={() => setRoomDetailsOpen(false)}
        room={selectedRoomForDetails}
        onBookRoom={handleBooking}
      />

      {/* Booking Modal */}
      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        roomData={selectedRoom}
        packageData={null}
      />
    </div>
  );
};

export default Rooms;